import React, { FC, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "redux/store";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import ButtonClose from "shared/ButtonClose/ButtonClose";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import ButtonThird from "shared/Button/ButtonThird";
import Checkbox from "shared/Checkbox/Checkbox";
import { AdjustmentsHorizontalIcon } from "@heroicons/react/24/outline";
import DatePicker from "react-datepicker";
import { setSearchData } from "redux/reducers/Search/SearchSlice";
import { useFormik } from "formik";
import { debounce } from "lodash";
import moment from "moment";
import {
  getPropertyCategories,
  getPropertyType,
  getAmenitiesProperty,
  getFeaturesProperty,
  searchCategory,
  getRoomType,
} from "../../redux/reducers/Properties/PropertiesSlice";
import { RoomType } from "redux/reducers/Properties/PropertiesSliceTypes";
import { getIconComponent } from "shared/Icon/IconDymamiclisting";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";

export interface TabFiltersMobileProps {
  page?: number;
  onPageReset?: () => void;
}

const TabFiltersMobile: FC<TabFiltersMobileProps> = ({ page, onPageReset }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<"filters" | "dates" | "price">(
    "filters"
  );

  const {
    propertiesCategories,
    propertyTypes,
    AmenitiesCategories,
    featuresCategories,
  } = useSelector((state: RootState) => state.property);

  const storedSearchData = useSelector(
    (state: RootState) => state.search.searchedData
  );

  const {
    latitude,
    longitude,
    label,
    startDate,
    endDate,
    roomTypeId,
    selectedAmenitiesId,
    propertyTypeId,
    priceMax,
    priceMin,
    bedroom,
    bathroom,
    propertyCategories,
    feature,
  } = storedSearchData || {};

  const [filteredPropertyTypes, setFilteredPropertyTypes] = useState<any[]>([]);
  const [priceRange, setPriceRange] = useState<[number, number]>(
    priceMin != null && priceMax != null ? [priceMin, priceMax] : [0, 5000]
  );
  const [roomTypes, setRoomTypes] = useState<RoomType[]>([]);
  const [datesRange, setDatesRange] = useState<[Date | null, Date | null]>([
    startDate ? new Date(startDate) : null,
    endDate ? new Date(endDate) : null,
  ]);

  useEffect(() => {
    dispatch(getPropertyCategories());
    dispatch(getPropertyType());
    dispatch(getAmenitiesProperty());
    dispatch(getFeaturesProperty());
  }, [dispatch]);

  useEffect(() => {
    const fetchRoomTypes = async (): Promise<void> => {
      try {
        const response = await dispatch(getRoomType()).unwrap();
        if (response.success) {
          setRoomTypes(response.data);
        }
      } catch (error) {
        console.error("Error fetching room types:", error);
      }
    };
    fetchRoomTypes();
  }, [dispatch]);

  useEffect(() => {
    if (Array.isArray(propertyTypes)) {
      setFilteredPropertyTypes(propertyTypes);
    }
  }, [propertyTypes]);

  // Sync price range from Redux to local state and formik
  useEffect(() => {
    if (priceMin !== undefined || priceMax !== undefined) {
      const newRange: [number, number] = [priceMin ?? 0, priceMax ?? 5000];

      // Only update if different from current state
      if (priceRange[0] !== newRange[0] || priceRange[1] !== newRange[1]) {
        setPriceRange(newRange);
        formik.setFieldValue("price_min", newRange[0]);
        formik.setFieldValue("price_max", newRange[1]);
        console.log("💰 TabFiltersMobile: Price synced from Redux:", newRange);
      }
    }
  }, [priceMin, priceMax]);

  // Sync property type IDs from Redux to formik
  useEffect(() => {
    if (propertyTypeId !== undefined) {
      const newPropertyTypeIds = propertyTypeId
        ? propertyTypeId.split(",").filter((id: string) => id.trim())
        : [];

      // Only update if different from current formik values
      const currentIds = formik.values.property_type_ids || [];
      if (JSON.stringify(currentIds) !== JSON.stringify(newPropertyTypeIds)) {
        formik.setFieldValue("property_type_ids", newPropertyTypeIds);
        console.log(
          "🏠 TabFiltersMobile: Property types synced from Redux:",
          newPropertyTypeIds
        );
      }
    }
  }, [propertyTypeId]);

  const formik = useFormik({
    initialValues: {
      location: label || "",
      startDate: startDate || "",
      endDate: endDate || "",
      property_category: Array.isArray(propertyCategories)
        ? propertyCategories
        : [],
      property_type_ids: propertyTypeId
        ? propertyTypeId.split(",").filter((id: string) => id.trim())
        : [],
      amenities: Array.isArray(selectedAmenitiesId) ? selectedAmenitiesId : [],
      features: Array.isArray(feature) ? feature : [],
      price_min: priceMin || priceRange[0],
      price_max: priceMax || priceRange[1],
      guest_number: "",
      bedrooms: bedroom || "",
      bathrooms: bathroom || "",
      room_type_id: roomTypeId || "",
      minStayMonths: null as number | null,
    },
    onSubmit: () => {},
  });

const triggerSearch = debounce(
  async (minPrice: number, maxPrice: number, values: any): Promise<void> => {
    const params = new URLSearchParams();

    // Location — always send lat/lng, plus neighborhood/city/radius from locationData
    if (latitude) params.append("q[latitude]", latitude.toString());
    if (longitude) params.append("q[longitude]", longitude.toString());

    if (storedSearchData?.locationData?.neighborhood) {
      params.append("q[neighborhood]", storedSearchData.locationData.neighborhood);
    }
    if (storedSearchData?.locationData?.city) {
      params.append("q[city]", storedSearchData.locationData.city);
    }
    const radius = storedSearchData?.locationData?.neighborhood ? 3 :
                   storedSearchData?.locationData?.city ? 5 : 10;
    params.append("q[radius]", radius.toString());

    // Price
    params.append("q[min_price]", minPrice.toString());
    params.append("q[max_price]", maxPrice.toString());

    // Dates
    const [localStart, localEnd] = datesRange;
    if (localStart) params.append("q[availability_start_lteq]", moment(localStart).format("YYYY-MM-DD"));
    if (localEnd) params.append("q[availability_end_gteq]", moment(localEnd).format("YYYY-MM-DD"));

    // Filters
    if (Array.isArray(values.property_type_ids) && values.property_type_ids.length > 0) {
      values.property_type_ids.forEach((id: string) => params.append("q[property_type_ids][]", id));
    }
    if (values.room_type_id) params.append("q[room_type_ids]", values.room_type_id);
    if (values.bedrooms) params.append("q[total_bedrooms_gteq]", values.bedrooms);
    if (values.bathrooms) params.append("q[total_bathrooms_gteq]", values.bathrooms);
    if (Array.isArray(values.amenities) && values.amenities.length > 0) {
      values.amenities.forEach((id: any) => params.append("q[amenity_ids][]", id));
    }
    if (Array.isArray(values.features) && values.features.length > 0) {
      values.features.forEach((id: any) => params.append("q[feature_ids][]", id));
    }
    if (page) params.append("page", page.toString());

    console.log("[TabFiltersMobile] triggerSearch params:", {
      locationData: storedSearchData?.locationData,
      neighborhood: storedSearchData?.locationData?.neighborhood,
      city: storedSearchData?.locationData?.city,
      radius,
      latitude,
      longitude,
      query: params.toString(),
    });

    try {
      const action = await dispatch(searchCategory(params.toString()));
      if (action.payload?.success) {
        // Reset to page 1 whenever filters change
        if (onPageReset) onPageReset();
        const normalised = {
          properties: action.payload?.properties || [],
          total_count: action.payload?.total_count || action.payload?.meta?.total_count || action.payload?.properties?.length || 0,
          total_pages: action.payload?.total_pages || action.payload?.meta?.total_pages || 1,
          current_page: action.payload?.current_page || action.payload?.meta?.current_page || 1,
          success: action.payload?.success,
        };
        dispatch(setSearchData({ ...storedSearchData, searchedResult: normalised }));
      }
    } catch (error) {
      console.error("[TabFiltersMobile] Search API error:", error);
    }
  },
  300
);

  const handleApplyFilters = async (): Promise<void> => {
    await triggerSearch(priceRange[0], priceRange[1], formik.values);
    setIsOpen(false);
  };

  const handlePriceChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    isMin: boolean
  ): void => {
    const value = Number(e.target.value);

    let newRange: [number, number];

    if (isMin) {
      if (value <= priceRange[1]) {
        newRange = [value, priceRange[1]];
        setPriceRange(newRange);
      } else {
        return;
      }
    } else {
      if (value >= priceRange[0]) {
        newRange = [priceRange[0], value];
        setPriceRange(newRange);
      } else {
        return;
      }
    }

    // Save to Redux immediately
    dispatch(
      setSearchData({
        ...storedSearchData,
        priceMin: newRange[0],
        priceMax: newRange[1],
      })
    );
  };

  const handleDateChange = (dates: [Date | null, Date | null]): void => {
    const [newStart, newEnd] = dates;
    setDatesRange([newStart, newEnd]);
    formik.setFieldValue("startDate", newStart);
    formik.setFieldValue("endDate", newEnd);

    dispatch(
      setSearchData({
        ...storedSearchData,
        startDate: newStart?.toISOString() ?? null,
        endDate: newEnd?.toISOString() ?? null,
      })
    );
  };

  const clearAllFilters = (): void => {
    setPriceRange([0, 5000]);
    setDatesRange([null, null]);
    formik.resetForm();
    dispatch(
      setSearchData({
        ...storedSearchData,
        propertyCategories: [],
        selectedAmenitiesId: [],
        feature: [],
        roomTypeId: "",
        propertyTypeId: "", // ← Add this line (empty string for Redux)
        priceMin: 0,
        priceMax: 5000,
        bedroom: "",
        bathroom: "",
        startDate: null,
        endDate: null,
      })
    );
  };

  const activeFiltersCount: number =
    (formik.values.property_type_ids?.length || 0) +
    (formik.values.property_category?.length || 0) +
    (formik.values.amenities?.length || 0) +
    (formik.values.features?.length || 0) +
    (formik.values.room_type_id ? 1 : 0) +
    (priceRange[0] !== 0 || priceRange[1] !== 5000 ? 1 : 0) +
    (formik.values.bedrooms ? 1 : 0) +
    (formik.values.bathrooms ? 1 : 0) +
    (datesRange[0] || datesRange[1] ? 1 : 0);

  const [start, end] = datesRange;

  return (
    <>
      {/* Filter Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="flex items-center justify-center gap-2 px-4 py-2.5 bg-white dark:bg-neutral-800 text-neutral-800 dark:text-neutral-200 border-2 border-neutral-300 dark:border-neutral-700 rounded-full font-semibold text-sm shadow-sm hover:shadow-md transition-all w-full"
      >
        <AdjustmentsHorizontalIcon className="w-5 h-5" />
        <span>Filters</span>
        {activeFiltersCount > 0 && (
          <span className="flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-primary-600 dark:bg-primary-500 text-white text-xs rounded-full font-bold">
            {activeFiltersCount}
          </span>
        )}
      </button>

      {/* Full Screen Filter Modal */}
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => setIsOpen(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-40 dark:bg-opacity-60" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-hidden">
            <div className="flex min-h-full items-center justify-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full bg-white dark:bg-neutral-900 flex flex-col" style={{ height: '100dvh' }}>
                  {/* Header */}
                  <div className="flex items-center justify-between px-4 py-4 border-b border-neutral-200 dark:border-neutral-800">
                    <button onClick={() => setIsOpen(false)}>
                      <ButtonClose />
                    </button>
                    <Dialog.Title className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                      Filters
                    </Dialog.Title>
                    <button
                      onClick={clearAllFilters}
                      className="text-sm font-semibold text-primary-600 dark:text-primary-400"
                    >
                      Clear all
                    </button>
                  </div>

                  {/* Tab Navigation */}
                  <div className="flex border-b border-neutral-200 dark:border-neutral-800">
                    <button
                      onClick={() => setActiveTab("filters")}
                      className={`flex-1 px-4 py-3 text-sm font-semibold transition-colors ${
                        activeTab === "filters"
                          ? "text-primary-600 dark:text-primary-400 border-b-2 border-primary-600 dark:border-primary-400"
                          : "text-neutral-600 dark:text-neutral-400"
                      }`}
                    >
                      Type & Amenities
                    </button>
                    <button
                      onClick={() => setActiveTab("price")}
                      className={`flex-1 px-4 py-3 text-sm font-semibold transition-colors ${
                        activeTab === "price"
                          ? "text-primary-600 dark:text-primary-400 border-b-2 border-primary-600 dark:border-primary-400"
                          : "text-neutral-600 dark:text-neutral-400"
                      }`}
                    >
                      Price & Rooms
                    </button>
                    <button
                      onClick={() => setActiveTab("dates")}
                      className={`flex-1 px-4 py-3 text-sm font-semibold transition-colors ${
                        activeTab === "dates"
                          ? "text-primary-600 dark:text-primary-400 border-b-2 border-primary-600 dark:border-primary-400"
                          : "text-neutral-600 dark:text-neutral-400"
                      }`}
                    >
                      Dates
                    </button>
                  </div>

                  {/* Content */}
                  <div className="flex-1 overflow-y-auto">
                    {/* FILTERS TAB */}
                    {activeTab === "filters" && (
                      <div className="p-4 space-y-6">
                        {/* Property Type */}

                        <div>
                          <h3 className="text-base font-semibold text-neutral-900 dark:text-neutral-100 mb-3">
                            Property Type
                          </h3>
                          <div className="space-y-2">
                            {filteredPropertyTypes.map((item: any) => {
                              const itemId = String(item.id);
                              const propertyTypeIds =
                                formik.values.property_type_ids || [];
                              const isChecked =
                                propertyTypeIds.includes(itemId);

                              return (
                                <div key={item.id}>
                                  <label className="flex items-center gap-2 cursor-pointer group">
                                    <input
                                      type="checkbox"
                                      checked={isChecked}
                                      onChange={(
                                        e: React.ChangeEvent<HTMLInputElement>
                                      ) => {
                                        const currentIds =
                                          formik.values.property_type_ids || [];
                                        const updatedIds = e.target.checked
                                          ? [...currentIds, itemId]
                                          : currentIds.filter(
                                              (id: string) => id !== itemId
                                            );

                                        formik.setFieldValue(
                                          "property_type_ids",
                                          updatedIds
                                        );
                                        dispatch(
                                          setSearchData({
                                            ...storedSearchData,
                                            propertyTypeId:
                                              updatedIds.join(","), // ← Convert array back to comma-separated string for Redux
                                          })
                                        );
                                      }}
                                      className="w-5 h-5 rounded border-2 border-neutral-300 dark:border-neutral-600 text-primary-600 focus:ring-primary-500 dark:bg-neutral-800 cursor-pointer transition-colors"
                                    />
                                    <span className="text-sm text-neutral-700 dark:text-neutral-300 group-hover:text-neutral-900 dark:group-hover:text-neutral-100 transition-colors">
                                      {item.name} ({item.property_count || 0})
                                    </span>
                                  </label>
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {/* Room Types */}
                        <div className="pt-6 border-t border-neutral-200 dark:border-neutral-800">
                          <h3 className="text-base font-semibold text-neutral-900 dark:text-neutral-100 mb-3">
                            Room Types
                          </h3>
                          <div className="space-y-2">
                            {roomTypes?.map((room: RoomType) => (
                              <div key={room.id}>
                                <Checkbox
                                  name="room_type_id"
                                  label={room.name}
                                  isChecked={
                                    formik.values.room_type_id ===
                                    String(room.id)
                                  }
                                  onChange={(e) => {
                                    const selectedRoomType = e
                                      ? String(room.id)
                                      : "";
                                    formik.setFieldValue(
                                      "room_type_id",
                                      selectedRoomType
                                    );
                                    dispatch(
                                      setSearchData({
                                        ...storedSearchData,
                                        roomTypeId: selectedRoomType,
                                      })
                                    );
                                  }}
                                />
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Amenities */}
                        <div className="pt-6 border-t border-neutral-200 dark:border-neutral-800">
                          <h3 className="text-base font-semibold text-neutral-900 dark:text-neutral-100 mb-3">
                            Amenities
                          </h3>
                          <div className="grid grid-cols-2 gap-3">
                            {AmenitiesCategories?.map((amenity: any) => (
                              <label
                                key={amenity.id}
                                className="flex items-center gap-2 cursor-pointer group"
                              >
                                <input
                                  type="checkbox"
                                  checked={formik.values.amenities?.includes(
                                    String(amenity.id)
                                  )}
                                  onChange={(
                                    e: React.ChangeEvent<HTMLInputElement>
                                  ) => {
                                    const selectedAmenities = e.target.checked
                                      ? [
                                          ...formik.values.amenities,
                                          String(amenity.id),
                                        ]
                                      : formik.values.amenities.filter(
                                          (a: string) =>
                                            a !== String(amenity.id)
                                        );
                                    formik.setFieldValue(
                                      "amenities",
                                      selectedAmenities
                                    );
                                    dispatch(
                                      setSearchData({
                                        ...storedSearchData,
                                        selectedAmenitiesId: selectedAmenities,
                                      })
                                    );
                                  }}
                                  className="w-5 h-5 rounded border-2 border-neutral-300 dark:border-neutral-600 text-primary-600 focus:ring-primary-500 dark:bg-neutral-800 cursor-pointer transition-colors"
                                />
                                <div className="flex items-center gap-2 text-neutral-700 dark:text-neutral-300 group-hover:text-neutral-900 dark:group-hover:text-neutral-100 transition-colors">
                                  {getIconComponent(amenity.icon_name)?.({
                                    className: "text-xl",
                                  })}
                                  <span className="text-sm">
                                    {amenity.name}
                                  </span>
                                </div>
                              </label>
                            ))}
                          </div>
                        </div>

                        {/* Property Features */}
                        <div className="pt-6 border-t border-neutral-200 dark:border-neutral-800">
                          <h3 className="text-base font-semibold text-neutral-900 dark:text-neutral-100 mb-3">
                            Property Features
                          </h3>
                          <div className="grid grid-cols-2 gap-3">
                            {featuresCategories?.map((item: any) => {
                              const idStr = String(item.id);
                              const isChecked =
                                formik.values.features?.includes(idStr);
                              return (
                                <label
                                  key={item.id}
                                  className="flex items-center gap-2 cursor-pointer group"
                                >
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={() => {
                                      let updatedFeatures: string[];
                                      if (isChecked) {
                                        updatedFeatures =
                                          formik.values.features.filter(
                                            (f: string) => f !== idStr
                                          );
                                      } else {
                                        updatedFeatures = [
                                          ...formik.values.features,
                                          idStr,
                                        ];
                                      }
                                      formik.setFieldValue(
                                        "features",
                                        updatedFeatures
                                      );
                                      dispatch(
                                        setSearchData({
                                          ...storedSearchData,
                                          feature: updatedFeatures,
                                        })
                                      );
                                    }}
                                    className="w-5 h-5 rounded border-2 border-neutral-300 dark:border-neutral-600 text-primary-600 focus:ring-primary-500 dark:bg-neutral-800 cursor-pointer transition-colors"
                                  />
                                  <div className="flex items-center gap-2 text-neutral-700 dark:text-neutral-300 group-hover:text-neutral-900 dark:group-hover:text-neutral-100 transition-colors">
                                    {getIconComponent(item.icon_name)?.({
                                      className: "text-xl",
                                    })}
                                    <span className="text-sm">{item.name}</span>
                                  </div>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* PRICE & ROOMS TAB */}
                    {activeTab === "price" && (
                      <div className="p-4 space-y-6">
                        {/* Price Range */}
                        <div>
                          <h3 className="text-base font-semibold text-neutral-900 dark:text-neutral-100 mb-4">
                            Price Range
                          </h3>
                          <div className="space-y-4">
                            {/* Slider Container */}
                            <div className="relative pt-1 pb-8 px-2">
                              {/* Background track */}
                              <div className="absolute top-1 left-2 right-2 h-2 bg-neutral-200 dark:bg-neutral-700 rounded-full" />

                              {/* Active range */}
                              <div
                                className="absolute top-1 h-2 bg-primary-600 dark:bg-primary-500 rounded-full transition-all duration-100"
                                style={{
                                  left: `calc(0.5rem + ${
                                    (priceRange[0] / 5000) * 100
                                  }%)`,
                                  right: `calc(0.5rem + ${
                                    100 - (priceRange[1] / 5000) * 100
                                  }%)`,
                                }}
                              />

                              {/* Min range input */}
                              <input
                                type="range"
                                min="0"
                                max="5000"
                                step="10"
                                value={priceRange[0]}
                                onChange={(e) => handlePriceChange(e, true)}
                                className="slider-thumb absolute w-full cursor-pointer"
                                style={{
                                  zIndex: priceRange[0] > 4000 ? 35 : 30,
                                  top: "-3px",
                                }}
                              />

                              {/* Max range input */}
                              <input
                                type="range"
                                min="0"
                                max="5000"
                                step="10"
                                value={priceRange[1]}
                                onChange={(e) => handlePriceChange(e, false)}
                                className="slider-thumb absolute w-full cursor-pointer"
                                style={{
                                  zIndex: 40,
                                  top: "-3px",
                                }}
                              />
                            </div>

                            {/* Price Display */}
                            <div className="flex items-center justify-between px-2">
                              <div className="flex-1">
                                <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-1">
                                  Minimum
                                </p>
                                <div className="bg-neutral-100 dark:bg-neutral-800 rounded-lg px-3 py-2">
                                  <p className="font-bold text-lg text-neutral-900 dark:text-neutral-100">
                                    ${priceRange[0].toLocaleString()}
                                  </p>
                                </div>
                              </div>

                              <div className="px-4">
                                <svg
                                  className="w-6 h-6 text-neutral-400"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M14 5l7 7m0 0l-7 7m7-7H3"
                                  />
                                </svg>
                              </div>

                              <div className="flex-1">
                                <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-1">
                                  Maximum
                                </p>
                                <div className="bg-neutral-100 dark:bg-neutral-800 rounded-lg px-3 py-2">
                                  <p className="font-bold text-lg text-neutral-900 dark:text-neutral-100">
                                    ${priceRange[1].toLocaleString()}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Bedrooms */}
                        <div className="pt-6 border-t border-neutral-200 dark:border-neutral-800">
                          <h3 className="text-base font-semibold text-neutral-900 dark:text-neutral-100 mb-3">
                            Bedrooms
                          </h3>
                          <div className="flex items-center gap-3">
                            <button
                              type="button"
                              onClick={() => {
                                const newValue = Math.max(
                                  0,
                                  Number(formik.values.bedrooms || 0) - 1
                                );
                                formik.setFieldValue("bedrooms", newValue);
                              }}
                              className="w-12 h-12 rounded-full border-2 border-neutral-300 dark:border-neutral-600 flex items-center justify-center text-neutral-700 dark:text-neutral-300 hover:border-primary-600 dark:hover:border-primary-500 transition-all text-2xl font-bold active:scale-95"
                            >
                              −
                            </button>
                            <span className="flex-1 text-center text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                              {formik.values.bedrooms || 0}
                            </span>
                            <button
                              type="button"
                              onClick={() => {
                                const newValue = Math.min(
                                  10,
                                  Number(formik.values.bedrooms || 0) + 1
                                );
                                formik.setFieldValue("bedrooms", newValue);
                              }}
                              className="w-12 h-12 rounded-full border-2 border-neutral-300 dark:border-neutral-600 flex items-center justify-center text-neutral-700 dark:text-neutral-300 hover:border-primary-600 dark:hover:border-primary-500 transition-all text-2xl font-bold active:scale-95"
                            >
                              +
                            </button>
                          </div>
                        </div>

                        {/* Bathrooms */}
                        <div className="pt-6 border-t border-neutral-200 dark:border-neutral-800">
                          <h3 className="text-base font-semibold text-neutral-900 dark:text-neutral-100 mb-3">
                            Bathrooms
                          </h3>
                          <div className="flex items-center gap-3">
                            <button
                              type="button"
                              onClick={() => {
                                const newValue = Math.max(
                                  0,
                                  Number(formik.values.bathrooms || 0) - 1
                                );
                                formik.setFieldValue("bathrooms", newValue);
                              }}
                              className="w-12 h-12 rounded-full border-2 border-neutral-300 dark:border-neutral-600 flex items-center justify-center text-neutral-700 dark:text-neutral-300 hover:border-primary-600 dark:hover:border-primary-500 transition-all text-2xl font-bold active:scale-95"
                            >
                              −
                            </button>
                            <span className="flex-1 text-center text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                              {formik.values.bathrooms || 0}
                            </span>
                            <button
                              type="button"
                              onClick={() => {
                                const newValue = Math.min(
                                  10,
                                  Number(formik.values.bathrooms || 0) + 1
                                );
                                formik.setFieldValue("bathrooms", newValue);
                              }}
                              className="w-12 h-12 rounded-full border-2 border-neutral-300 dark:border-neutral-600 flex items-center justify-center text-neutral-700 dark:text-neutral-300 hover:border-primary-600 dark:hover:border-primary-500 transition-all text-2xl font-bold active:scale-95"
                            >
                              +
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                   {activeTab === "dates" && (
  <div className="p-4 space-y-4 overflow-y-auto">
    <h3 className="text-base font-semibold text-neutral-900 dark:text-neutral-100">
      Select Dates
    </h3>

    <style>{`
      .ndp-mobile .react-datepicker {
        border: none !important;
        background: transparent !important;
        font-family: inherit !important;
        width: 100% !important;
      }
      .ndp-mobile .react-datepicker__month-container {
        width: 100% !important;
        float: none !important;
      }
      .ndp-mobile .react-datepicker__header {
        padding: 0 !important;
        border: none !important;
        background: transparent !important;
      }
      .ndp-mobile .react-datepicker__day-names {
        background: #fff4f1;
        margin: 0 !important;
        padding: 4px 0 !important;
        display: flex !important;
        justify-content: space-around !important;
      }
      .dark .ndp-mobile .react-datepicker__day-names {
        background: #1f2937 !important;
      }
      .ndp-mobile .react-datepicker__day-name {
        color: #f5694b !important;
        font-weight: 700 !important;
        font-size: 11px !important;
        text-transform: uppercase !important;
        flex: 1 !important;
        text-align: center !important;
        line-height: 2rem !important;
      }
      .ndp-mobile .react-datepicker__week {
        display: flex !important;
        justify-content: space-around !important;
      }
      .ndp-mobile .react-datepicker__month {
        margin: 8px 4px !important;
      }
      .ndp-mobile .react-datepicker__day {
        flex: 1 !important;
        height: 2.5rem !important;
        line-height: 2.5rem !important;
        font-size: 15px !important;
        margin: 2px !important;
        border-radius: 50% !important;
        text-align: center !important;
        color: #111827 !important;
      }
      .dark .ndp-mobile .react-datepicker__day {
        color: #e5e7eb !important;
      }
      .ndp-mobile .react-datepicker__day:hover:not(.react-datepicker__day--disabled) {
        background: #fff4f1 !important;
        color: #f5694b !important;
        border-radius: 50% !important;
      }
      .ndp-mobile .react-datepicker__day--disabled {
        color: #d1d5db !important;
        background: transparent !important;
        cursor: not-allowed !important;
        opacity: 0.45 !important;
      }
      .dark .ndp-mobile .react-datepicker__day--disabled {
        color: #4b5563 !important;
        opacity: 1 !important;
      }
      .ndp-mobile .react-datepicker__day--outside-month {
        color: #e5e7eb !important;
        pointer-events: none;
      }
      .dark .ndp-mobile .react-datepicker__day--outside-month {
        color: #374151 !important;
      }
    `}</style>

    <div className="ndp-mobile rounded-xl overflow-hidden border border-neutral-200 dark:border-neutral-700">
      <DatePicker
        selected={start}
        onChange={handleDateChange}
        startDate={start}
        endDate={end}
        selectsRange
        inline
        monthsShown={1}
        minDate={new Date()}
        dayClassName={(date) => {
          const isStart = start && date.toDateString() === start.toDateString();
          const isEnd = end && date.toDateString() === end.toDateString();
          const inRange = start && end && date > start && date < end;
          const isToday = date.toDateString() === new Date().toDateString();
          if (isStart || isEnd) return "!bg-[#f5694b] !text-white font-bold";
          if (inRange) return "!bg-[#fff4f1] dark:!bg-[#3d1f18] !text-[#c94a30] dark:!text-[#f5694b]";
          if (isToday) return "font-bold !text-[#f5694b]";
          return "";
        }}
        renderCustomHeader={({
          monthDate,
          decreaseMonth,
          increaseMonth,
          prevMonthButtonDisabled,
          nextMonthButtonDisabled,
        }) => (
          <div className="flex items-center justify-between px-3 py-3 bg-gradient-to-r from-[#f5694b] to-[#e2583d] relative overflow-hidden">
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                backgroundImage:
                  "repeating-linear-gradient(45deg, rgba(255,255,255,0.07) 0px, rgba(255,255,255,0.07) 1px, transparent 1px, transparent 10px)",
              }}
            />
            <button
              onClick={decreaseMonth}
              disabled={prevMonthButtonDisabled}
              type="button"
              className={`relative z-10 w-8 h-8 flex items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30 transition ${prevMonthButtonDisabled ? "opacity-40 cursor-not-allowed" : ""}`}
            >
              <ChevronLeftIcon className="w-5 h-5" />
            </button>
            <span className="relative z-10 text-white font-bold text-base tracking-wide">
              {monthDate.toLocaleString("en-US", { month: "long", year: "numeric" })}
            </span>
            <button
              onClick={increaseMonth}
              disabled={nextMonthButtonDisabled}
              type="button"
              className={`relative z-10 w-8 h-8 flex items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30 transition ${nextMonthButtonDisabled ? "opacity-40 cursor-not-allowed" : ""}`}
            >
              <ChevronRightIcon className="w-5 h-5" />
            </button>
          </div>
        )}
      />
    </div>

    {start && (
      <div className="p-4 bg-[#fff4f1] dark:bg-[#3d1f18] rounded-xl border border-[#f5694b]/20">
        <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
          Selected Range
        </p>
        <p className="text-sm text-[#f5694b] font-medium mt-1">
          {start.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
          {end ? ` → ${end.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}` : " → Pick end date"}
        </p>
      </div>
    )}
  </div>
)}
                  </div>

                  {/* Footer */}
                  <div className="border-t border-neutral-200 dark:border-neutral-800 p-4 flex gap-3">
                    <ButtonThird
                      onClick={clearAllFilters}
                      sizeClass="flex-1 px-6 py-3"
                    >
                      Clear All
                    </ButtonThird>
                    <ButtonPrimary
                      onClick={handleApplyFilters}
                      sizeClass="flex-1 px-6 py-3"
                    >
                      Show Results
                    </ButtonPrimary>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default TabFiltersMobile;
